/* Quick view swatch-fix marker: QV_SWATCH_FIX_V3 */
class QuickViewDrawer extends MenuDrawer {
  constructor() {
    super();

    this.setClasses({
      open: 'quick-view--open',
      opening: 'quick-view--opening',
      closing: 'quick-view--closing'
    });

    // Remember where the drawer lives inside the product card so it can be
    // restored there after the quick view closes (see moveToBody).
    this._originalParent = null;
    this._originalNextSibling = null;

    // Scroll position saved when the body scroll-lock is applied (see
    // lockBodyScroll/unlockBodyScroll), so it can be restored on close.
    this._scrollY = 0;

    /*
     * FIX (popup closing when a color swatch is clicked): remember where the
     * current pointer press STARTED. When a swatch is clicked, product-info.js
     * re-renders the popup content asynchronously (fetch + viewTransition),
     * which hides the old picker and 500ms later removes it. If that DOM swap
     * happens BETWEEN the mousedown and mouseup of a click, the browser
     * re-targets the click event to the nearest common ancestor of the press
     * and release targets — which can be the <quick-view class="quick-view">
     * backdrop element itself (real close via the overlay handler below) or
     * <body>/a detached node (real close via MenuDrawer.onBodyClick). Whether
     * the swap lands inside that tiny window is pure timing, which is why the
     * popup only closed *sometimes*.
     *
     * A press that begins inside the sheet (.quick-view__content) is ALWAYS a
     * content interaction, so the matching click may never close the drawer,
     * no matter where the re-render re-targets it. Backdrop taps and presses
     * that begin outside the sheet still close as before. Both mousedown and
     * touchstart are tracked because iOS fires touchstart first and may not
     * fire a usable mousedown until the tap emulation runs.
     */
    this._pressStartedInContent = false;

    const trackPressOrigin = (event) => {
      const target = event.target;
      this._pressStartedInContent = !!(
        target &&
        target.closest &&
        target.closest('.quick-view__content')
      );
    };

    this.addEventListener('mousedown', trackPressOrigin);
    this.addEventListener('touchstart', trackPressOrigin, { passive: true });

    /*
     * PRIMARY FIX (popup closing when a color swatch inside it is clicked —
     * reproduced live on the storefront).
     *
     * Every quick view popup is rendered from the same section
     * (main-product-modal), so the swatch input IDs inside the popup are the
     * SAME on every product (template--…__main-1-0/-1-1/…). A popup that was
     * closed while its content fetch was still in flight gets refilled by the
     * late response, so hidden leftover drawers with those IDs can exist in
     * other product cards. While a popup is open it is portaled to the END of
     * <body>, so `document.getElementById(inputId)` — which is exactly how the
     * browser resolves a <label for> click — finds ANOTHER CARD'S leftover
     * input FIRST, and the browser forwards the swatch click to that foreign,
     * closed drawer. The forwarded click bubbles from there to <body>, where
     * the OPEN drawer's onBodyClick sees a target it does not contain and
     * closes the popup. Whether a leftover drawer earlier in the DOM exists
     * depends on scroll history and timing — which is why the popup only
     * closed *sometimes*.
     *
     * Fix: intercept label clicks in the CAPTURE phase (before the browser's
     * default label activation runs). If the label lives in this popup's sheet
     * but its for-ID resolves to an element OUTSIDE the sheet, cancel the
     * default activation and click OUR OWN matching input instead. The swatch
     * behaves exactly as intended and the click can no longer escape the
     * popup. Labels whose control resolves inside the sheet are untouched, so
     * normal clicking (no ID collision) behaves exactly as before.
     */
    this.addEventListener('click', (event) => {
      const label = event.target.closest && event.target.closest('label[for]');
      if (!label) return;

      const sheet = this.querySelector('.quick-view__content');
      if (!sheet || !sheet.contains(label)) return;

      const forId = label.getAttribute('for');
      if (!forId) return;

      let resolved = null;
      try {
        resolved = document.getElementById(forId);
      } catch (e) {
        return;
      }

      // Normal case: the label's control is inside this popup. Do nothing.
      if (resolved && sheet.contains(resolved)) return;

      // The ID collided with markup outside this popup — redirect the
      // activation to our own input so the click stays inside the drawer.
      let ours = null;
      try {
        ours = sheet.querySelector('input[id="' + (window.CSS && CSS.escape ? CSS.escape(forId) : forId) + '"]');
      } catch (e) {
        ours = null;
      }
      if (!ours) return; // nothing to redirect to — fall back to browser default

      event.preventDefault();
      ours.click();
    }, true);

    /*
     * The overlay is now `pointer-events: auto` (it used to be `none`, which
     * let backdrop taps fall through to the product card underneath). Because
     * the overlay is inside the drawer, MenuDrawer's own outside-click check
     * (`!this.contains(event.target)`) no longer fires for it, so close it
     * explicitly when the tap landed on the backdrop itself rather than on
     * the sheet or the close button.
     *
     * Guard: if the press that produced this click started INSIDE the sheet,
     * this click is a re-render-retargeted content interaction (see the
     * press-origin note above), not a real backdrop tap — keep the popup
     * open. A genuine backdrop tap starts on the backdrop itself, so its
     * press origin is never inside the sheet and closing still works.
     */
    this.addEventListener('click', (event) => {
      if (event.target.classList && event.target.classList.contains('quick-view')) {
        if (this._pressStartedInContent) return;
        this.dispatchEvent(new CustomEvent('drawer:force-close', { bubbles: true }));
      }
    });

    this.addEventListener('close', () => {
      const drawerContent = this.querySelector('.quick-view__content');

      // Put the close button back inside the content wrapper before wiping it,
      // so nothing is left orphaned on the overlay between opens.
      const strayClose = this.querySelector('.quick-view > .quick-view__close');
      if (strayClose) strayClose.remove();

      drawerContent.innerHTML = '';
      drawerContent.classList.remove('hide-cover');

      // Return the drawer to its original position in the card so the card
      // markup (and the hover quick view trigger) stays intact for the next open.
      this.moveFromBody();
      this.unlockBodyScroll();

      document.dispatchEvent(new CustomEvent('quickview:close'));
    });
  }

  /*
   * iOS Safari fix: the theme's default scroll lock just sets
   * `overflow: hidden` on <body> (see the `quick-view--open` rule in
   * component-quick-view.css and setClasses above). iOS Safari has always
   * ignored plain `overflow: hidden` on <body> for blocking touch-scrolling,
   * so swiping inside the (tall, internally-scrollable) quick view content
   * scrolled the real page underneath instead of just the drawer's own
   * scroll area — which is why the actual homepage sections above and below
   * the trigger could slide into view behind what should have been a fixed,
   * full-screen overlay, and why the fixed close button drifted out of
   * place with it.
   *
   * The reliable cross-browser fix is to pin <body> itself with
   * `position: fixed` and a negative `top` offset equal to the current
   * scroll position while the drawer is open, then restore the scroll
   * position on close. This still relies on the `overflow: hidden` class
   * for non-iOS browsers, so it's applied in addition to (not instead of)
   * the existing class-based lock.
   */
  lockBodyScroll() {
    if (document.body.style.position === 'fixed') return;
    this._scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  unlockBodyScroll() {
    if (document.body.style.position !== 'fixed') return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, this._scrollY);
  }

  /*
   * iOS Safari fix: `position: fixed` overlays are clipped or trapped when
   * they are rendered inside overflow / scrollable ancestors. On the home page
   * tabbed collections the quick view sits inside
   *   .tab-content          { overflow: hidden }
   *   .slider-mobile-gutter { overflow-x: auto; overflow-y: hidden;
   *                            -webkit-overflow-scrolling: touch }
   * WebKit incorrectly clips fixed-position descendants to those boxes
   * (see WebKit bug 160953), so on iPhone the "fixed" quick view renders
   * inline inside the tab area, scrolls with the page, and the fixed close
   * button disappears. Chrome/Android (and the listing page, which has no
   * overflow wrappers) are unaffected — which is exactly the reported bug.
   *
   * Fix: move the drawer to <body> right before it opens, and move it back
   * after it closes. At body level the overlay always covers the viewport and
   * no ancestor overflow / stacking context can clip it.
   */
  moveToBody() {
    if (this.parentNode !== document.body) {
      this._originalParent = this.parentNode;
      this._originalNextSibling = this.nextElementSibling;

      // Expose the product url so QuickViewButton can still locate the drawer
      // while it is portaled to <body> (e.g. during the close animation).
      const quickViewEl = this.querySelector('quick-view');
      if (quickViewEl && quickViewEl.dataset.productUrl) {
        this.dataset.productUrl = quickViewEl.dataset.productUrl;
      }

      document.body.appendChild(this);
    }
  }

  moveFromBody() {
    if (this._originalParent && this._originalParent.isConnected && this.parentNode !== this._originalParent) {
      if (this._originalNextSibling && this._originalNextSibling.parentNode === this._originalParent) {
        this._originalParent.insertBefore(this, this._originalNextSibling);
      } else {
        this._originalParent.appendChild(this);
      }
    }
    this._originalParent = null;
    this._originalNextSibling = null;
  }

  openMenuDrawer(summaryElement = false) {
    // Escape overflow/clipping ancestors (iOS Safari) before opening.
    this.moveToBody();
    this.lockBodyScroll();
    super.openMenuDrawer(summaryElement);
  }

  /*
   * Safety net for focus churn caused by the swatch re-render (see the
   * press-origin note in the constructor for the primary fix).
   *
   * Hiding/removing the focused swatch input (HTMLUpdateUtility.viewTransition)
   * drops focus to <body> (WebKit/Firefox) or re-targets it to the
   * tabindex="-1" .quick-view__content wrapper (Blink) and fires `focusout`.
   * product-info.js restores focus to the re-rendered input
   * (`document.querySelector('#' + targetId)?.focus()`), but that restore
   * loses the race or is skipped in several real cases:
   *   - the re-rendered input carries `disabled` (unavailable swatch / sold
   *     out combination), and a disabled input cannot take focus at all.
   *   - the picker renders as a dropdown: the restore target is an <option>,
   *     and <option> elements are never focusable.
   *   - a sibling-product swap renders different input ids, so the restore
   *     finds nothing.
   *   - the render callback throws before its focus step (the restore lives
   *     in a separate .then, so it never runs after a throw).
   *
   * Focus parked on <body>/<html> or on a not-yet-replaced detached node is
   * therefore NOT a signal that the user left the drawer — it is a transient
   * state of the swatch re-render. This override keeps the popup open for
   * those transient states; a focusout whose target sits inside a subtree the
   * re-render just hid (inline display:none, exactly what viewTransition sets)
   * is ignored outright. Focus genuinely moving to a live element outside the
   * drawer keeps the base class behavior (same closeMenuDrawer call).
   * Escape, the close button, the backdrop tap and add-to-cart flows are all
   * handled elsewhere and keep working unchanged.
   */
  onFocusOut(event) {
    // Render-induced focus loss: the element losing focus sits under a node
    // that viewTransition just hid (inline display:none) — ignore completely.
    const losing = event && event.target;
    if (losing && this.contains(losing)) {
      if (!losing.isConnected) return;

      let node = losing;
      while (node && node !== this) {
        if (node.nodeType === Node.ELEMENT_NODE && node.style && node.style.display === 'none') return;
        node = node.parentNode;
      }
    }

    setTimeout(() => {
      if (!this.mainDetailsToggle.hasAttribute('open')) return;

      const active = document.activeElement;

      // Transient re-render states: focus dropped to <body>/<html>, or is
      // still reported as a detached node by some engines. Keep the popup
      // open; product-info.js restores focus to the re-rendered input.
      if (!active || active === document.body || active === document.documentElement) return;
      if (active.nodeType === Node.ELEMENT_NODE && !active.isConnected) return;

      // Focus is on (or was restored to) an element inside the drawer: normal.
      if (this.mainDetailsToggle.contains(active)) return;

      // Focus genuinely moved to an element outside the drawer: keep the base
      // menu-drawer focus-out behavior (same call the base class makes).
      this.closeMenuDrawer();
    });
  }

  /*
   * Defensive guards for the swatch re-render and for cross-drawer click
   * leakage (see the label-redirect note in the constructor for the primary
   * fix). The base onBodyClick closes the drawer for any click whose target
   * is not inside it. The stale cases that must never close:
   *   1. The click target was already detached from the document (emulated
   *      touch click arriving after the swatch change replaced the DOM).
   *      `this.contains()` returns false for detached nodes, which would
   *      misread the stale click as an outside click.
   *   2. The press that produced this click STARTED inside the sheet and the
   *      re-render re-targeted the click to <body>/<html> or another node
   *      outside this drawer (the press/release targets lost their common
   *      ancestor when the old picker node was removed). The press-origin
   *      tracking in the constructor identifies these; they are content
   *      interactions, not the user clicking away.
   *   3. The click landed inside a DIFFERENT quick view drawer (leftover
   *      hidden drawer markup in another product card — e.g. a label-forward
   *      that resolved there before the redirect guard could stop it). Such
   *      clicks are invisible to the user and must never close this popup.
   * Everything else keeps the base outside-click-to-close behavior.
   */
  onBodyClick(event) {
    if (event.target && event.target.nodeType === Node.ELEMENT_NODE && !event.target.isConnected) return;

    if (this._pressStartedInContent && event.target && this.contains(event.target) === false) return;

    if (event.target && event.target.closest && event.target.closest('quick-view-drawer') &&
        event.target.closest('quick-view-drawer') !== this) return;

    super.onBodyClick(event);
  }

  /*
   * Guard against a race in the base MenuDrawer: when the drawer is closed
   * while the 400ms open animation is still running, the open animation
   * completion re-adds the `quick-view--open` body class after the close
   * flow removed it, leaving the page scroll-locked. If the body is already
   * in the closing state when the open animation completes, clean up instead
   * of re-opening the body classes.
   */
  openAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else if (document.body.classList.contains(this.classes.closing)) {
        // Closed while opening: drop the stale opening class and do not re-open.
        document.body.classList.remove(this.classes.opening);
      } else {
        document.body.classList.remove(this.classes.opening);
        document.body.classList.add(this.classes.open);
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}
customElements.define('quick-view-drawer', QuickViewDrawer);

class QuickViewButton extends MenuDrawer {
  constructor() {
    super();

    // Timestamp of the last touch that was handled by the touchend fallback,
    // used to skip the duplicate "ghost" click on devices where both fire.
    this._touchHandledAt = 0;

    const openDrawer = () => {
      const wrapper = this.closest('.card-wrapper');
      let drawer = wrapper ? wrapper.querySelector('quick-view-drawer') : null;

      // Fallback: the drawer may still be attached to <body> (portal) while
      // its close animation runs — find it by product url instead.
      if (!drawer && this.dataset.productUrl) {
        try {
          drawer = document.querySelector(
            'quick-view-drawer[data-product-url="' + CSS.escape(this.dataset.productUrl) + '"]'
          );
        } catch (e) {
          /* CSS.escape unavailable — ignore and use the link fallback below. */
        }
      }

      if (drawer) {
        const summary = drawer.querySelector('summary');
        if (summary) {
          summary.click();
        }

        document.dispatchEvent(new CustomEvent('quickview:open', {
          detail: {
            productUrl: this.dataset.productUrl
          }
        }));
      } else if (this.dataset.productUrl) {
        window.location.href = this.dataset.productUrl;
      }
    };

    // Prevent default click
    const handleTrigger = (event) => {
      // Prevent parent <a> links or default image link triggers on iOS
      event.preventDefault();
      event.stopPropagation();

      // Skip if a recent touch already opened the drawer (ghost click).
      if (window.performance.now() - this._touchHandledAt < 600) return;

      openDrawer();
    };

    this.addEventListener('click', handleTrigger);

    /*
     * iOS Safari fallback: inside draggable scroll-snap sliders some code paths
     * (or older slider scripts) call preventDefault() on the synthesized
     * mousedown, which cancels the click event entirely on iPhone. Handle taps
     * directly on touchend instead: if the finger did not move (i.e. it was a
     * tap, not a swipe/scroll) we open the quick view ourselves and cancel the
     * emulated click to avoid a double-open.
     */
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;
    let touchTracked = false;

    this.addEventListener('touchstart', (event) => {
      touchTracked = false;
      if (event.touches.length !== 1) return;
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
      touchStartTime = Date.now();
      touchTracked = true;
    }, { passive: true });

    this.addEventListener('touchend', (event) => {
      if (!touchTracked || event.changedTouches.length !== 1) return;
      touchTracked = false;

      const touch = event.changedTouches[0];
      const moved = Math.hypot(touch.clientX - touchStartX, touch.clientY - touchStartY);
      const elapsed = Date.now() - touchStartTime;

      // A swipe/scroll or a long press: let the browser handle it.
      if (moved > 10 || elapsed > 500) return;

      this._touchHandledAt = window.performance.now();

      // Handle the tap ourselves and stop the emulated click (avoids double-open).
      event.preventDefault();
      openDrawer();
    });
  }
}
customElements.define('quick-view-button', QuickViewButton);

class QuickView extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Keep a single observer when the element is re-inserted in the DOM
    // (the drawer is moved to <body> while the quick view is open).
    if (this._observer) this._observer.disconnect();
    this._observer = new IntersectionObserver(this.handleIntersection.bind(this));
    this._observer.observe(this);
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
  }

  handleIntersection(entries, _observer) {
    if (!entries[0].isIntersecting) return;

    const selector = '.quick-view__content';
    const drawerContent = this.querySelector(selector);

    // Guard: the element is re-observed when the drawer is teleported to
    // <body> on open. If the product was already fetched, do not fetch again.
    if (drawerContent.children.length > 0 || this._fetching) return;

    const productUrl = this.dataset.productUrl.split('?')[0];
    const sectionUrl = `${productUrl}?view=modal`;
    this._fetching = true;

    fetch(sectionUrl)
      .then(response => response.text())
      .then(responseText => {
        setTimeout(() => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const productElement = responseHTML.querySelector(selector);
          this.setInnerHTML(drawerContent, productElement.innerHTML);

          this.detachCloseButton(drawerContent);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
        }, 200);

        setTimeout(() => {
          drawerContent.classList.add('hide-cover');

          // Safety net in case the markup arrived late (variant re-render etc).
          this.detachCloseButton(drawerContent);

          document.dispatchEvent(new CustomEvent('quickview:loaded', {
            detail: {
              productUrl: this.dataset.productUrl
            }
          }));
        }, 500);
      })
      .catch(e => {
        console.error(e);
      })
      .finally(() => {
        this._fetching = false;
      });
  }

  /*
   * iOS Safari fix for the close button.
   *
   * The `drawer-close-button.quick-view__close` markup lives inside
   * `.quick-view__content` in sections/main-product-modal.liquid, and that is
   * the element quick view uses as its scroll container
   * (`overflow-y: auto`). A `position: fixed` element inside a WebKit
   * scroller is clipped to that scroller and its resolved position drifts as
   * the user scrolls, so on iPhone the button either rode up under the
   * status bar or vanished entirely. Android/Chrome resolve it correctly,
   * which is why the bug only ever showed on iOS.
   *
   * Lifting it one level up to `.quick-view` — which is the fixed, full
   * screen overlay and therefore a containing block for absolutely
   * positioned children — takes it out of the scroller completely. The CSS
   * then pins it with an explicit `top` inside the padding band the overlay
   * reserves at the top. The `drawer:force-close` event it fires still
   * bubbles to `quick-view-drawer`, so closing keeps working unchanged.
   */
  detachCloseButton(drawerContent) {
    const closeButton = drawerContent.querySelector('.quick-view__close');
    if (!closeButton) return;

    // Drop a stale button from a previous open before adding the new one.
    const existing = this.querySelector(':scope > .quick-view__close');
    if (existing) existing.remove();

    this.appendChild(closeButton);
  }

  setInnerHTML(element, html) {
    element.innerHTML = html;

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
customElements.define('quick-view', QuickView);